import { Button } from "$components/button/button";
import { IconButton } from "$components/button/icon-button";
import { ConditionalDisplay } from "$components/conditional-display";
import { Grid, Menu, MenuItem } from "@material-ui/core";
import PrintIcon from "@material-ui/icons/Print";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { usePermissions } from "../../../hooks/usePermissions";
import { type FinalisedActionsProps, FinalisedStatus } from "./types";

export const FinalisedActions = ({
  printInvoice,
  refundInvoice,
  voidInvoice,
  takePayment,
  paymentHistory,
  finalisedStatus,
}: FinalisedActionsProps) => {
  const { formatMessage } = useIntl();
  const permissions = usePermissions();

  const PrintIconButton = () => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClose = () => {
      setAnchorEl(null);
    };
    const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const handleSelect = (print: () => void) => {
      handleClose();
      print();
    };

    if (printInvoice?.hide) return null;

    return (
      <>
        <IconButton
          onClick={handleOpen}
          aria-label={formatMessage({
            id: "invoicing.invoice.finalisedActions.print.choice",
            defaultMessage: "Print Receipt or Invoice",
          })}
        >
          <PrintIcon fontSize="large" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem
            onClick={() => handleSelect(printInvoice.handlePrintInvoice)}
          >
            <FormattedMessage
              id="invoicing.invoice.finalisedActions.print.invoice"
              defaultMessage="Print Invoice"
            />
          </MenuItem>
          <ConditionalDisplay
            condition={
              finalisedStatus !== FinalisedStatus.UNPAID &&
              finalisedStatus !== FinalisedStatus.PARTIALLY_PAID &&
              finalisedStatus !== FinalisedStatus.PENDING
            }
          >
            <MenuItem
              onClick={() => handleSelect(printInvoice.handlePrintReceipt)}
            >
              <FormattedMessage
                id="invoicing.invoice.finalisedActions.print.receipt"
                defaultMessage="Print Receipt"
              />
            </MenuItem>
          </ConditionalDisplay>
        </Menu>
      </>
    );
  };

  const RefundButton = () => (
    <ConditionalDisplay
      condition={refundInvoice && !refundInvoice.hide && permissions.refunds}
    >
      <Button color="error" onClick={refundInvoice?.handleAction}>
        <FormattedMessage
          id="invoicing.invoice.finalisedActions.refund"
          defaultMessage="Refund"
        />
      </Button>
    </ConditionalDisplay>
  );

  const TakePaymentButton = () => (
    <ConditionalDisplay condition={!takePayment?.hide}>
      <Button color="primary" onClick={takePayment?.handleAction}>
        <FormattedMessage
          id="invoicing.invoice.finalisedActions.takePayment"
          defaultMessage="Take Payment"
        />
      </Button>
    </ConditionalDisplay>
  );

  const PaymentHistoryButton = () => (
    <ConditionalDisplay condition={!paymentHistory?.hide}>
      <Button color="secondary" onClick={paymentHistory?.handleAction}>
        <FormattedMessage
          id="invoicing.invoice.finalisedActions.paymentHistory"
          defaultMessage="Payment History"
        />
      </Button>
    </ConditionalDisplay>
  );

  const VoidButton = () => (
    <ConditionalDisplay condition={permissions.void && !voidInvoice?.hide}>
      <Button color="error" onClick={voidInvoice?.handleAction}>
        <FormattedMessage
          id="invoicing.invoice.finalisedActions.void"
          defaultMessage="Void"
        />
      </Button>
    </ConditionalDisplay>
  );

  return (
    <Grid container justifyContent="space-between" alignItems="center">
      <Grid item>
        <PrintIconButton />
      </Grid>
      <Grid item style={{ display: "flex", gap: "16px" }}>
        <ConditionalDisplay
          condition={finalisedStatus === FinalisedStatus.PAID}
        >
          <PaymentHistoryButton />
          <RefundButton />
        </ConditionalDisplay>

        <ConditionalDisplay
          condition={finalisedStatus === FinalisedStatus.PARTIALLY_PAID}
        >
          <PaymentHistoryButton />
          <RefundButton />
          <TakePaymentButton />
        </ConditionalDisplay>

        <ConditionalDisplay
          condition={finalisedStatus === FinalisedStatus.UNPAID}
        >
          <VoidButton />
          <TakePaymentButton />
        </ConditionalDisplay>

        <ConditionalDisplay
          condition={finalisedStatus === FinalisedStatus.PENDING}
        >
          <VoidButton />
          <TakePaymentButton />
        </ConditionalDisplay>

        <ConditionalDisplay
          condition={finalisedStatus === FinalisedStatus.PARTIALLY_REFUNDED}
        >
          <PaymentHistoryButton />
          <RefundButton />
        </ConditionalDisplay>

        <ConditionalDisplay
          condition={
            finalisedStatus === FinalisedStatus.REFUNDED ||
            finalisedStatus === FinalisedStatus.VOID
          }
        >
          <PaymentHistoryButton />
        </ConditionalDisplay>
      </Grid>
    </Grid>
  );
};
